import { type FC, useState } from 'react';

import {
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  VStack,
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaCaretDown } from 'react-icons/fa';
import { LuMenu, LuX } from 'react-icons/lu';
import { useLocation } from 'react-router-dom';

import { colors } from '@/theme/foundations/colors';

type ProductSwitcherProps = {
  isCollapsed: boolean;
};

export const ProductSwitcher: FC<ProductSwitcherProps> = ({ isCollapsed }) => {
  const location = useLocation();
  const { t } = useTranslation(['components']);

  const application: {
    id: 'ATS' | 'interviews';
    title: string;
  } = location.pathname.startsWith('/admin/boards')
    ? { id: 'ATS', title: t('components:board.ATSBoards') }
    : { id: 'interviews', title: t('components:layout.interviews') };

  const handleOpenNewTab = (slug: `/${string}`) => {
    const fullUrl = `${window.location.origin}${slug}`;
    window.open(fullUrl, '_blank');
  };

  const [isOpen, setIsOpen] = useState(false);

  return (
    <Menu
      placement="bottom-end"
      onOpen={() => {
        setIsOpen(true);
      }}
      onClose={() => {
        setIsOpen(false);
      }}
    >
      <MenuButton
        bgColor="transparent"
        border={isCollapsed ? 'none' : '1px solid'}
        borderColor="gray.100"
        borderRadius="8px"
        w="full"
        py={2}
        px={2}
        bg={isOpen ? 'brand.25' : 'transparent'}
        _hover={{ bg: isOpen ? 'brand.50' : 'gray.50' }}
      >
        <HStack gap={2}>
          {!isCollapsed ? (
            <>
              <VStack gap={1} flexGrow={1} align="start">
                <Text fontSize="lg" fontWeight="semibold">
                  {application.title}
                </Text>
              </VStack>
              <motion.div
                animate={{ rotate: !isOpen ? 0 : 180 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
              >
                <FaCaretDown fontSize="20px" color={colors.gray[500]} />
              </motion.div>
            </>
          ) : (
            <motion.div
              key={isOpen ? 'close' : 'menu'}
              initial={{ opacity: 0, rotate: isOpen ? -90 : 90 }}
              animate={{ opacity: 1, rotate: 0 }}
              exit={{ opacity: 0, rotate: isOpen ? 90 : -90 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
            >
              {isOpen ? (
                <LuX size={27} color={colors.gray[500]} />
              ) : (
                <LuMenu size={27} color={colors.gray[500]} />
              )}
            </motion.div>
          )}
        </HStack>
      </MenuButton>

      <MenuList
        w="268px"
        overflow="hidden"
        p={3}
        shadow="md"
        borderRadius={12}
        borderColor="gray.100"
      >
        <VStack gap={3}>
          <MenuItem
            p={2}
            border="1px solid"
            borderColor="gray.100"
            transition="linear 0.1s"
            borderRadius={8}
            fontWeight="medium"
            onClick={() => {
              if (application.id === 'interviews') return;
              handleOpenNewTab('/admin');
            }}
            _hover={{
              borderColor: 'brand.100',
              bg: 'brand.25',
            }}
          >
            {t('components:layout.interviews')}
          </MenuItem>

          <MenuItem
            p={2}
            border="1px solid"
            borderColor="gray.100"
            transition="linear 0.1s"
            borderRadius={8}
            fontWeight="medium"
            onClick={() => {
              if (application.id === 'ATS') return;
              handleOpenNewTab('/admin/boards');
            }}
            _hover={{
              borderColor: 'brand.100',
              bg: 'brand.25',
            }}
          >
            ATS
          </MenuItem>
        </VStack>
      </MenuList>
    </Menu>
  );
};
